import { useState, useEffect, useLayoutEffect, useCallback } from "react";
import type { ReactNode } from "react";
import api from "../utils/api";

const Deposits = () => {
  const [depositData, setDepositData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  /** Reset page before fetch effect runs so pagination stays aligned with search/filters (same tick as layout). */
  useLayoutEffect(() => {
    setPage(1);
  }, [search, statusFilter, limit]);

  const fetchDeposits = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search,
      });
      if (statusFilter !== "All") {
        params.set("status", statusFilter);
      }
      const response = await api.get(`/api/deposit/getDeposit?${params.toString()}`);

      if (response.data.status === "success") {
        const deposits =
          response.data.deposits ||
          (response.data.Deposit ? Object.values(response.data.Deposit) : []);
        setDepositData(deposits);
        const p = response.data.pagination;
        if (p) {
          setTotal(p.total ?? 0);
          setTotalPages(Math.max(0, p.totalPages ?? 0));
        }
      }
    } catch (error) {
      console.error("Error fetching deposits:", error);
      setDepositData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  const handleAction = async (id: string, email: string, status: string) => {
    if (!window.confirm(`Are you sure you want to mark this deposit as ${status}?`)) return;

    try {
      setIsLoading(true);
      await api.post(`/api/deposit/updateDeposit/${id}`, { status });
      alert(`Deposit ${status} successfully`);
      await fetchDeposits();
    } catch (error) {
      console.error("Error updating deposit:", error);
      alert("Failed to update deposit");
    } finally {
      setIsLoading(false);
    }
  };

  const safeTotalPages = Math.max(1, totalPages || 1);
  const showPagination = total > 0 && totalPages > 0;

  return (
    <div className="max-w-6xl mx-auto p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Deposit Requests</h1>

      {/* Search & Filter Controls */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <input
          type="text"
          placeholder="Search by Email, Order ID, TX ID..."
          className="border rounded-lg p-3 w-full md:w-1/2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <div className="flex gap-2 w-full md:w-auto">
          <select
            className="border rounded-lg p-3 flex-grow md:flex-none md:w-40 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Success">Success</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
          </select>

          <select
            value={limit}
            onChange={(e) => {
              const newLimit = parseInt(e.target.value, 10);
              setLimit(newLimit);
            }}
            className="p-3 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-300"
          >
            <option value="10">10 / page</option>
            <option value="20">20 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
        </div>
      </div>

      {/* Deposit List */}
      <div className="space-y-6">
        {isLoading && depositData.length === 0 ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : depositData.length > 0 ? (
          depositData.map((request: any, index: number) => (
            <div
              key={request._id || index}
              className="bg-white shadow-lg rounded-xl p-4 md:p-6 border border-gray-200 w-full hover:shadow-xl transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm md:text-lg font-bold text-gray-800 truncate">{request.email}</h2>
                  <p className="text-[10px] md:hidden text-gray-400 mt-0.5">
                    {new Date(request.createdAt).toLocaleString("en-GB")}
                  </p>
                </div>
                <span
                  className={`shrink-0 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-tight ${
                    request.status.toLowerCase() === "success"
                      ? "bg-green-100 text-green-700"
                      : request.status.toLowerCase() === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-700"
                  }`}
                >
                  {request.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-y-3 gap-x-4 md:gap-6 text-xs md:text-sm">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[9px] md:text-xs text-gray-400 font-bold uppercase">Amount</span>
                  <span className="text-green-600 font-black text-sm md:text-base">
                    ${parseFloat(request.amount).toLocaleString()}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[9px] md:text-xs text-gray-400 font-bold uppercase">Chain</span>
                  <span className="text-gray-700 font-medium truncate">{request.chain}</span>
                </div>
                <div className="hidden md:block">
                  <span className="text-[9px] md:text-xs text-gray-400 font-bold uppercase">Time</span>
                  <span className="text-gray-600 truncate block">
                    {new Date(request.createdAt).toLocaleString("en-GB")}
                  </span>
                </div>
                <div className="col-span-1">
                  <span className="text-[9px] md:text-xs text-gray-400 font-bold uppercase">Order ID</span>
                  <span className="text-gray-600 truncate block">{request.orderid}</span>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <span className="text-[9px] md:text-xs text-gray-400 font-bold uppercase">Deposit Address</span>
                  <span className="text-gray-600 truncate block">{request.deposit_address}</span>
                </div>
                {request.txid && (
                  <div className="col-span-2 md:col-span-1">
                    <span className="text-[9px] md:text-xs text-gray-400 font-bold uppercase">TX ID</span>
                    <span className="text-gray-600 truncate block">{request.txid}</span>
                  </div>
                )}
              </div>

              {request.status.toLowerCase() === "pending" && (
                <div className="mt-4 flex gap-4">
                  <button
                    onClick={() => handleAction(request._id, request.email, "Success")}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleAction(request._id, request.email, "Rejected")}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center">No deposits found.</p>
        )}
      </div>

      {/* Pagination — totals match server filter + search */}
      {!isLoading && showPagination && (
        <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-8 mb-4">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700"
            >
              Previous
            </button>

            <div className="flex items-center space-x-1 flex-wrap justify-center">
              {(() => {
                const pages: ReactNode[] = [];
                const maxVisible = 5;
                let start = Math.max(1, page - Math.floor(maxVisible / 2));
                let end = Math.min(safeTotalPages, start + maxVisible - 1);

                if (end - start + 1 < maxVisible) {
                  start = Math.max(1, end - maxVisible + 1);
                }

                if (start > 1) {
                  pages.push(
                    <button
                      key={1}
                      type="button"
                      onClick={() => setPage(1)}
                      className="w-10 h-10 rounded-lg border hover:bg-gray-50 transition-colors text-gray-600"
                    >
                      1
                    </button>
                  );
                  if (start > 2) pages.push(<span key="dots-start" className="px-1 text-gray-400">...</span>);
                }

                for (let i = start; i <= end; i++) {
                  if (i <= 0) continue;
                  pages.push(
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i)}
                      className={`w-10 h-10 rounded-lg border transition-all ${
                        page === i
                          ? "bg-blue-600 text-white border-blue-600 shadow-md transform scale-105"
                          : "hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      {i}
                    </button>
                  );
                }

                if (end < safeTotalPages) {
                  if (end < safeTotalPages - 1)
                    pages.push(<span key="dots-end" className="px-1 text-gray-400">...</span>);
                  pages.push(
                    <button
                      key={safeTotalPages}
                      type="button"
                      onClick={() => setPage(safeTotalPages)}
                      className="w-10 h-10 rounded-lg border hover:bg-gray-50 transition-colors text-gray-600"
                    >
                      {safeTotalPages}
                    </button>
                  );
                }

                return pages;
              })()}
            </div>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(safeTotalPages, p + 1))}
              disabled={page >= safeTotalPages}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700"
            >
              Next
            </button>
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border shadow-sm">
            <span>Go to:</span>
            <input
              type="number"
              min={1}
              max={safeTotalPages}
              className="w-16 p-1 border rounded text-center focus:ring focus:ring-blue-100 outline-none"
              placeholder="#"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const p = parseInt((e.target as HTMLInputElement).value, 10);
                  if (!isNaN(p) && p >= 1 && p <= safeTotalPages) {
                    setPage(p);
                  }
                }
              }}
            />
            <span className="whitespace-nowrap">
              of {safeTotalPages} ({total} total)
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposits;
